<template>
  <div
    v-if="showModal"
    class="fixed inset-0 z-[9999] flex items-center justify-center p-3 sm:p-4 bg-black/75 backdrop-blur-sm transition-opacity"
    role="dialog"
    aria-modal="true"
    aria-labelledby="privacy-modal-title"
  >
    <div
      class="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[92vh] flex flex-col overflow-hidden border border-gray-100 animate-fadeIn"
    >
      <template v-if="state === 'review'">
        <div class="px-6 py-5 border-b border-gray-100 flex items-center justify-between bg-white flex-shrink-0">
          <div class="flex items-center gap-3">
            <img src="/images/Logo1.png" alt="ipNX Logo" class="h-7 sm:h-8" />
            <div class="h-5 w-[1px] bg-gray-300 hidden sm:block"></div>
            <h2 id="privacy-modal-title" class="text-base sm:text-lg font-bold font-poppins text-gray-900">
              Recruitment Data Privacy Notice
            </h2>
          </div>
          <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-50 text-pigmentred border border-red-100">
            Action Required
          </span>
        </div>

        <div class="p-6 overflow-y-auto font-poppins text-gray-700 space-y-6 text-sm sm:text-[15px] leading-relaxed bg-[#FCFCFD]">
          <div class="bg-blue-50/70 border border-blue-200/60 rounded-xl p-4 text-blue-900 text-xs sm:text-sm">
            <p class="font-medium">
              Welcome to the ipNX Careers Portal. Please review and accept our Recruitment Data Privacy Notice to proceed.
            </p>
          </div>

          <div>
            <h3 class="text-base sm:text-lg font-bold text-gray-900 mb-2">
              ipNX Recruitment Data Privacy Notice
            </h3>
            <p class="mb-3">
              At ipNX, we are committed to protecting your personal information and ensuring that your data is processed
              responsibly, securely, and in accordance with applicable data protection laws, including the Nigeria Data
              Protection Act (NDPA).
            </p>
            <p>
              This Privacy Notice explains how we collect, use, store, and protect your personal information when you
              apply for a role with ipNX.
            </p>
          </div>

          <div class="border-t border-gray-200 pt-4">
            <h4 class="font-bold text-gray-900 mb-2">What Information Do We Collect?</h4>
            <p class="mb-2">As part of our recruitment process, we may collect information such as:</p>
            <ul class="space-y-1.5 list-none pl-0">
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Full name</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Contact information (email address, phone number, and residential address)</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Curriculum Vitae (CV) or résumé</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Educational qualifications and certifications</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Employment history and professional experience</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Skills, competencies, and references</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Information provided during interviews, assessments, or recruitment activities</span>
              </li>
            </ul>
            <p class="mt-3 text-xs sm:text-sm text-gray-600">
              Where required by law or for specific roles, we may also collect additional information such as identity
              verification documents, background verification details, or pre-employment screening information.
            </p>
          </div>

          <div class="border-t border-gray-200 pt-4">
            <h4 class="font-bold text-gray-900 mb-2">Why Do We Collect Your Information?</h4>
            <p class="mb-2">We process your personal information to:</p>
            <ul class="space-y-1.5 list-none pl-0">
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Assess your suitability for employment opportunities at ipNX</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Manage and administer the recruitment and selection process</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Communicate with you regarding your application</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Verify qualifications, references, and employment history where applicable</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Comply with legal, regulatory, and corporate governance requirements</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Maintain records relating to recruitment activities</span>
              </li>
            </ul>
          </div>

          <div class="border-t border-gray-200 pt-4">
            <h4 class="font-bold text-gray-900 mb-2">How Do We Collect Your Information?</h4>
            <p class="mb-2">We may collect your information directly from you through:</p>
            <ul class="space-y-1.5 list-none pl-0">
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Our careers portal</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Application forms</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>CVs and supporting documents</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Interviews and assessments</span>
              </li>
            </ul>
            <p class="mt-3 text-xs sm:text-sm text-gray-600">
              We may also obtain information from third parties such as recruitment agencies, referees, background
              verification providers, or publicly available professional platforms where appropriate.
            </p>
          </div>

          <div class="border-t border-gray-200 pt-4">
            <h4 class="font-bold text-gray-900 mb-2">Who Do We Share Your Information With?</h4>
            <p class="mb-2">Your information may be shared only where necessary with:</p>
            <ul class="space-y-1.5 list-none pl-0">
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Authorized ipNX employees involved in the recruitment process</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Recruitment and assessment service providers</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Background screening or verification partners</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Regulatory authorities, law enforcement agencies, or other parties where required by law</span>
              </li>
            </ul>
            <p class="mt-2 font-semibold text-gray-900">We do not sell your personal information.</p>
          </div>

          <div class="border-t border-gray-200 pt-4">
            <h4 class="font-bold text-gray-900 mb-2">How Long Do We Keep & Protect Your Information?</h4>
            <p class="mb-2">
              If your application is unsuccessful, we may retain your information for a reasonable period to support
              future opportunities, comply with legal obligations, and maintain recruitment records. If you are hired,
              relevant information provided during the recruitment process may become part of your employee records.
            </p>
            <p>
              ipNX implements appropriate technical, administrative, and organizational safeguards to protect your personal
              information against unauthorized access, disclosure, alteration, loss, or misuse.
            </p>
          </div>

          <div class="border-t border-gray-200 pt-4">
            <h4 class="font-bold text-gray-900 mb-2">Your Rights</h4>
            <p class="mb-2">Subject to applicable law, your rights include:</p>
            <ul class="space-y-1.5 list-none pl-0">
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Request access to your personal information</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Request correction of inaccurate information</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Request deletion of your information where applicable</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Withdraw consent where processing is based on consent</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Object to or restrict certain processing activities</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Lodge a complaint regarding the processing of your personal information to the NDPC</span>
              </li>
              <li class="flex items-start gap-2">
                <img src="/images/checkmark-red.png" alt="checkmark" class="w-3.5 h-3.5 mt-1 flex-shrink-0" />
                <span>Request for information relating to the scope of the data processing</span>
              </li>
            </ul>
          </div>

          <div class="border-t border-gray-200 pt-4 bg-gray-100/70 p-4 rounded-xl">
            <h4 class="font-bold text-gray-900 mb-1">Consent Acknowledgment</h4>
            <p class="text-xs sm:text-sm text-gray-700 leading-relaxed">
              By submitting your application through the ipNX Careers Portal, you acknowledge that you have read and
              understood this Privacy Notice and consent to the processing of your personal information for recruitment
              and employment related purposes, where such consent is required by applicable law.
            </p>
          </div>
        </div>

        <div class="px-6 py-4 bg-white border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-3 flex-shrink-0">
          <p class="text-xs text-gray-500 font-poppins text-center sm:text-left">
            You must accept this notice to proceed with using the recruitment portal.
          </p>
          <div class="flex items-center gap-3 w-full sm:w-auto">
            <button
              type="button"
              @click="declinePolicy"
              class="w-1/2 sm:w-auto px-5 py-2.5 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 font-poppins text-sm font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-gray-300"
            >
              Decline
            </button>
            <button
              type="button"
              @click="acceptPolicy"
              class="w-1/2 sm:w-auto px-6 py-2.5 rounded-lg bg-pigmentred hover:bg-[#c8141b] text-white font-poppins text-sm font-semibold shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-pigmentred"
            >
              Accept &amp; Proceed
            </button>
          </div>
        </div>
      </template>

      <template v-else-if="state === 'declined'">
        <div class="p-8 sm:p-12 text-center font-poppins">
          <div class="w-16 h-16 sm:w-20 sm:h-20 bg-red-100 text-pigmentred rounded-full flex items-center justify-center mx-auto mb-6">
            <svg class="w-8 h-8 sm:w-10 sm:h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"></path>
            </svg>
          </div>

          <h2 class="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Access Restricted
          </h2>

          <p class="text-gray-600 max-w-lg mx-auto text-sm sm:text-base leading-relaxed mb-8">
            You have declined the ipNX Recruitment Data Privacy Notice. In compliance with the Nigeria Data Protection
            Act (NDPA) and our recruitment policies, your consent is required to access and use the Careers Portal.
          </p>

          <div class="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              type="button"
              @click="state = 'review'"
              class="w-full sm:w-auto px-6 py-3 rounded-lg bg-pigmentred hover:bg-[#c8141b] text-white font-semibold text-sm transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-pigmentred"
            >
              Review &amp; Accept Notice
            </button>

            <a
              href="https://www.ipnxnigeria.net/"
              class="w-full sm:w-auto px-6 py-3 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-100 font-semibold text-sm transition-colors text-center"
            >
              Exit to Corporate Website
            </a>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import VueCookies from "vue-cookies";

export default {
  name: "PrivacyPolicyModal",
  data() {
    return {
      showModal: false,
      state: "review",
    };
  },
  mounted() {
    this.checkPolicyStatus();
  },
  watch: {
    showModal(val) {
      if (val) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    },
  },
  methods: {
    checkPolicyStatus() {
      const acceptedCookie = VueCookies.get("privacyPolicyAccepted");
      const acceptedStorage = localStorage.getItem("privacyPolicyAccepted");

      if (acceptedCookie === "true" || acceptedStorage === "true") {
        this.showModal = false;
        document.body.style.overflow = "";
      } else {
        this.showModal = true;
        this.state = "review";
        document.body.style.overflow = "hidden";
      }
    },
    acceptPolicy() {
      VueCookies.set("privacyPolicyAccepted", "true", 365);
      localStorage.setItem("privacyPolicyAccepted", "true");
      this.showModal = false;
      document.body.style.overflow = "";
    },
    declinePolicy() {
      VueCookies.remove("privacyPolicyAccepted");
      localStorage.removeItem("privacyPolicyAccepted");
      this.state = "declined";
      this.showModal = true;
      document.body.style.overflow = "hidden";
    },
  },
  beforeUnmount() {
    document.body.style.overflow = "";
  },
};
</script>

<style scoped>
@keyframes fadeIn {
  from {
    opacity: 0;
    transform: scale(0.97);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

.animate-fadeIn {
  animation: fadeIn 0.2s ease-out forwards;
}
</style>
